import { Animations, Sex } from "@/enums/characterGeneratorEnums";
import { SheetDefinition } from "@/types/sheet-definiation";
import { useImageInstance, useSpriteSheetUrl } from "@/use/common";
import { ref } from "vue";
import type { Media } from "@/types/entity";
import api from "@/modules/api";

// 动画帧数映射
const animationFrameCounts: Record<string, number> = {
  spellcast: 7,
  thrust: 8,
  walk: 9,
  slash: 6,
  shoot: 13,
  hurt: 6,
  climb: 6,
  idle: 2,
  jump: 5,
  sit: 3,
  emote: 3,
  run: 8,
  combat_idle: 2,
  backslash: 13,
  halfslash: 7,
};

// 定义图层类型
interface ItemLayer {
  definition: SheetDefinition;
  url: string;
  zPos: number;
  variant: string;
}

export function useSpriteSheetExport() {
  // 常量定义
  const FRAME_SIZE = 64;
  const DIRECTIONS = 4;
  const isExporting = ref(false);

  // 预加载所有精灵表图片
  const preloadAllSpritesheets = async (
    sortedLayers: ItemLayer[]
  ): Promise<Record<string, HTMLImageElement>> => {
    const loadedImages: Record<string, HTMLImageElement> = {};

    const loadPromises = sortedLayers.map((layer) => {
      return new Promise<void>((resolve) => {
        const image = useImageInstance();
        const url = useSpriteSheetUrl(
          layer.url,
          selectedAnimation.value,
          layer.variant
        );

        image.src = url;
        image.crossOrigin = "anonymous";

        image.onload = () => {
          loadedImages[layer.url] = image;
          resolve();
        };

        image.onerror = () => {
          console.error(` ${url}`);
          resolve(); // 即使失败也继续
        };
      });
    });

    await Promise.all(loadPromises);
    return loadedImages;
  };

  // 选择的动画类型
  const selectedAnimation = ref<Animations>(Animations.Walk);

  // 获取按zPos排序的图层
  const getSortedItems = (
    defList: SheetDefinition[],
    selectedSex: Sex
  ): ItemLayer[] => {
    const layers: ItemLayer[] = [];

    for (const item of defList) {
      // 获取该项的所有图层
      for (const key of Object.keys(item)) {
        if (key.includes("layer_")) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const layer = item[key] as any; // 临时使用any绕过类型检查
          const sexKey = selectedSex;

          if (
            layer &&
            typeof layer === "object" &&
            "zPos" in layer &&
            sexKey in layer
          ) {
            const url = layer[sexKey];
            if (typeof url === "string") {
              const variantIndex =
                typeof item.selectedVariant === "number"
                  ? item.selectedVariant
                  : 0;

              layers.push({
                definition: item,
                url,
                zPos: layer.zPos,
                variant: item.variants[variantIndex],
              });
            }
          }
        }
      }
    }

    // 按zPos排序
    return layers.sort((a, b) => a.zPos - b.zPos);
  };

  // 合并所有选择项的精灵表并导出
  const mergeAndExportSpritesheet = async (
    defList: SheetDefinition[],
    selectedSex: Sex
  ): Promise<string | null> => {
    if (defList.length === 0) return null;

    isExporting.value = true;

    try {
      // 获取当前动画的帧数
      const frameCount =
        animationFrameCounts[selectedAnimation.value.toLowerCase()] || 9;

      // 创建完整的精灵表Canvas: 宽 = 帧数 * 帧宽度, 高 = 方向数 * 帧高度
      const tempCanvas = document.createElement("canvas");
      tempCanvas.width = frameCount * FRAME_SIZE;
      tempCanvas.height = DIRECTIONS * FRAME_SIZE;
      const ctx = tempCanvas.getContext("2d");

      if (!ctx) {
        console.error("cannot get canvas context");
        return null;
      }

      // 按照zPos排序图层
      const sortedItems = getSortedItems(defList, selectedSex);

      // 预加载所有图片 - 完整的精灵表
      const loadedImages = await preloadAllSpritesheets(sortedItems);

      // 清空画布
      ctx.clearRect(0, 0, tempCanvas.width, tempCanvas.height);

      // 绘制每个图层的完整精灵表
      for (const layer of sortedItems) {
        const image = loadedImages[layer.url];
        if (image) {
          // 对每一帧和每个方向分别绘制
          for (let dir = 0; dir < DIRECTIONS; dir++) {
            for (let frame = 0; frame < frameCount; frame++) {
              const srcX = frame * FRAME_SIZE;
              const srcY = dir * FRAME_SIZE;
              const destX = frame * FRAME_SIZE;
              const destY = dir * FRAME_SIZE;

              // 绘制特定的一帧
              ctx.drawImage(
                image,
                srcX,
                srcY,
                FRAME_SIZE,
                FRAME_SIZE, // 源
                destX,
                destY,
                FRAME_SIZE,
                FRAME_SIZE // 目标
              );
            }
          }
        }
      }

      // 将Canvas转换为图片并下载
      const dataUrl = tempCanvas.toDataURL("image/png");
      isExporting.value = false;
      return dataUrl;
    } catch (error) {
      console.error("merge sprite sheet failed:", error);
      isExporting.value = false;
      return null;
    }
  };

  // 合并精灵表并上传到服务器
  const mergeAndUploadSpritesheet = async (
    defList: SheetDefinition[],
    selectedSex: Sex
  ): Promise<Media | null> => {
    try {
      // 先合并精灵表获取数据URL
      const dataUrl = await mergeAndExportSpritesheet(defList, selectedSex);
      if (!dataUrl) return null;
      // 将数据URL转换为Blob/File对象
      const response = await fetch(dataUrl);
      const blob = await response.blob();
      const file = new File([blob], "character_sprite.png", {
        type: "image/png",
      });
      // 导入API并上传文件
      const mediaType = 0; // 对应Mate头像的MediaType
      const result = await api.uploadMedia(mediaType, file);
      if (result?.item) {
        return result.item;
      }
      return null;
    } catch (error) {
      console.error("upload sprite sheet failed:", error);
      return null;
    }
  };

  return {
    selectedAnimation,
    isExporting,
    mergeAndExportSpritesheet,
    mergeAndUploadSpritesheet,
    getSortedItems,
  };
}
